import { useCallback, useEffect, useMemo, useReducer } from "react";
import { PositionStructurePojo } from "cuba/entities/PositionStructurePojo";
import { getCubaREST } from "@cuba-platform/react";
import { TreeNodeNormal } from "antd/lib/tree/Tree";
import { highlight } from "../../../util/treeUtil";
import { PositionExt } from "cuba/entities/base/base$PositionExt";
import { restServices } from "cuba/services";

type State = {
  treeData: PositionStructurePojo[];
  root: PositionStructurePojo | null;
  expandedKeys: string[];
  additionalNames: Map<string, string>;
  searchValue: string;
};

const initialState: State = {
  treeData: [],
  root: null,
  expandedKeys: [],
  additionalNames: new Map(),
  searchValue: "",
};

function reducer(state: State, action: any): State {
  switch (action.type) {
    case "RESET":
      return { ...initialState };
    case "SET_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

export const usePositionHierarchyTree = (
  rootPositionId: string,
  changeSelectedPosition: (positionId?: string) => void,
  getDefaultId?: (id: string) => void,
  formatAdditionalName?: (count: string) => string,
) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { treeData, root, expandedKeys, additionalNames, searchValue } = state;

  const hasLoadedChildren = (id: string) =>
    treeData.some((item) => item.parentPositionId === id);

  const loadedKeys = useMemo(
    () =>
      root
        ? [
            root.position.id,
            ...treeData
              .filter((i) => hasLoadedChildren(i.position.id))
              .map((i) => i.position.id),
          ]
        : [],
    [treeData, root],
  );

  const makeName = useCallback(
    (item: PositionStructurePojo) => {
      const additionalName = additionalNames.get(item.positionGroupId);
      const name = item.position?._instanceName || item.position?.positionName;
      return additionalName && formatAdditionalName
        ? `${name} ${formatAdditionalName(additionalName)}`
        : name;
    },
    [additionalNames, formatAdditionalName],
  );

  const makeNode = useCallback(
    (item: PositionStructurePojo): TreeNodeNormal => ({
      title: highlight(makeName(item), searchValue),
      key: item.position.id,
      isLeaf: !item.hasChildren,
      children: treeData
        .filter((c) => c.parentPositionId === item.position.id)
        .map(makeNode),
    }),
    [makeName, treeData, searchValue],
  );

  const nodes = useMemo(() => (root ? [makeNode(root)] : []), [root, makeNode]);

  const findGroupById = (positionId: string) =>
    [...(root ? [root] : []), ...treeData].find(
      (i) => i.position?.id === positionId,
    )?.positionGroupId ?? null;

  const onExpand = useCallback(
    (keys: string[]) =>
      dispatch({ type: "SET_STATE", payload: { expandedKeys: keys } }),
    [],
  );

  const onSelect = useCallback(
    (selectedKeys: React.Key[]) =>
      changeSelectedPosition(findGroupById(selectedKeys[0] as string)),
    [changeSelectedPosition, root, treeData],
  );

  const loadChild = async (positionId: string) => {
    const children =
      await restServices.positionStructureService.getStructureChildren(
        positionId,
      );
    dispatch({
      type: "SET_STATE",
      payload: { treeData: [...children] },
    });
    return children;
  };

  const loadAdditionalNames = async (ids: string[]) => {
    if (ids.length === 0) return;
    const result =
      await restServices.hierarchyService.getPositionAdditionalNameByPositionGroup(
        ids,
      );
    dispatch({ type: "SET_STATE", payload: { additionalNames: result } });
  };

  const init = async () => {
    dispatch({ type: "RESET" });

    const rootPosition = await getCubaREST().loadEntity<PositionExt>(
      PositionExt.NAME,
      rootPositionId,
      { view: "position-edit" },
    );

    const rootPojo: PositionStructurePojo = {
      parentPositionId: null,
      position: rootPosition,
      hasChildren: true,
      positionGroupId: rootPosition.group.id,
    };

    if (getDefaultId) {
      getDefaultId(rootPojo.positionGroupId);
    }

    const children = await loadChild(rootPojo.position.id);
    await loadAdditionalNames([
      rootPojo.positionGroupId,
      ...children.map((c) => c.positionGroupId),
    ]);

    dispatch({
      type: "SET_STATE",
      payload: { root: rootPojo, expandedKeys: [rootPojo.position.id] },
    });
  };

  const onSearch = async (value: string) => {
    dispatch({ type: "SET_STATE", payload: { searchValue: value } });
    if (!root) return;

    if (!value) {
      await init();
      return;
    }

    const filtered =
      await restServices.positionStructureService.getStructureChildren(
        root.position.id,
        value,
      );

    dispatch({
      type: "SET_STATE",
      payload: {
        treeData: filtered,
        expandedKeys: [root.position.id, ...filtered.map((i) => i.position.id)],
      },
    });
  };

  useEffect(() => {
    init();
  }, [rootPositionId]);

  return {
    nodes,
    expandedKeys,
    loadedKeys,
    onExpand,
    onSelect,
    onSearch,
  };
};
